import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import {
  Loader2, ChevronDown, ChevronRight, SlidersHorizontal,
  RotateCcw, Save, FolderOpen, Trash2, X, Rows3, Trophy,
  Sparkles, AlertTriangle, Lightbulb, History, FileText, FileSpreadsheet,
} from 'lucide-react';
import { MODIFIER_DEFS, COMPLEXITY_OPTIONS } from './compare/scenarioConstants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CompareScenarioPanel({
  currencySymbol = '$',
  idA, idB, nameA, nameB, originalData,
  scenarioActive, setScenarioActive,
  data, setData,
}) {
  const [scenarioOpen, setScenarioOpen] = useState(false);
  const [scenarioTarget, setScenarioTarget] = useState('b');
  const [scenarioComplexity, setScenarioComplexity] = useState('');
  const [scenarioModifiers, setScenarioModifiers] = useState({});
  const [scenarioLoading, setScenarioLoading] = useState(false);
  const debounceRef = useRef(null);

  // Saved scenarios
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [saveName, setSaveName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const loadRef = useRef(null);

  // Matrix
  const [matrixSelected, setMatrixSelected] = useState([]);
  const [matrixData, setMatrixData] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const [matrixExporting, setMatrixExporting] = useState(false);

  // AI Recommendation
  const [recPriority, setRecPriority] = useState('balanced');
  const [recData, setRecData] = useState(null);
  const [recLoading, setRecLoading] = useState(false);
  const [recHistory, setRecHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/scenarios`, { withCredentials: true })
      .then(res => setSavedScenarios(res.data?.scenarios || [])).catch(() => {});
    axios.get(`${API_URL}/api/scenarios/recommendation-history`, { withCredentials: true })
      .then(res => setRecHistory(res.data?.history || [])).catch(() => {});
  }, []);

  // Initialize from original data
  useEffect(() => {
    if (!originalData) return;
    const targetEst = scenarioTarget === 'a' ? originalData.a?.estimate : originalData.b?.estimate;
    if (targetEst) {
      setScenarioComplexity(targetEst.complexity || 'standard');
      setScenarioModifiers(targetEst.modifiers || {});
    }
    if (scenarioActive) {
      setData(originalData);
      setScenarioActive(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioTarget, originalData]);

  // Close load dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (loadRef.current && !loadRef.current.contains(e.target)) setLoadOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const runScenario = async (complexity, modifiers) => {
    if (!idA || !idB) return;
    setScenarioLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/export/compare-quotes-scenario`, {
        id_a: idA, id_b: idB,
        overrides: { target: scenarioTarget, complexity, modifiers },
      }, { withCredentials: true });
      setData(res.data);
      setScenarioActive(true);
    } catch { /* error */ }
    finally { setScenarioLoading(false); }
  };

  const handleModifierChange = (key, value) => {
    const updated = { ...scenarioModifiers, [key]: value };
    setScenarioModifiers(updated);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runScenario(scenarioComplexity, updated), 400);
  };

  const handleComplexityChange = (value) => {
    setScenarioComplexity(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runScenario(value, scenarioModifiers), 400);
  };

  const resetScenario = () => {
    if (!originalData) return;
    const targetEst = scenarioTarget === 'a' ? originalData.a?.estimate : originalData.b?.estimate;
    setScenarioComplexity(targetEst?.complexity || 'standard');
    setScenarioModifiers(targetEst?.modifiers || {});
    setData(originalData);
    setScenarioActive(false);
  };

  const handleSaveScenario = async () => {
    if (!saveName.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/scenarios`, {
        name: saveName.trim(), target: scenarioTarget,
        complexity: scenarioComplexity, modifiers: scenarioModifiers,
      }, { withCredentials: true });
      setSavedScenarios(prev => [res.data, ...prev]);
      setSaveName(''); setShowSaveInput(false);
    } catch { /* error */ }
  };

  const handleLoadScenario = (scenario) => {
    setScenarioTarget(scenario.target || 'b');
    setScenarioComplexity(scenario.complexity || 'standard');
    setScenarioModifiers(scenario.modifiers || {});
    setLoadOpen(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runScenario(scenario.complexity, scenario.modifiers), 200);
  };

  const handleDeleteScenario = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/scenarios/${id}`, { withCredentials: true });
      setSavedScenarios(prev => prev.filter(s => s.id !== id));
      setMatrixSelected(prev => prev.filter(sid => sid !== id));
    } catch { /* error */ }
  };

  const toggleMatrixSelect = (id) => {
    setMatrixSelected(prev => prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]);
  };

  const runMatrix = async () => {
    if (matrixSelected.length < 2 || (!idA && !idB)) return;
    setMatrixLoading(true); setMatrixOpen(true);
    try {
      const res = await axios.post(`${API_URL}/api/scenarios/compare-matrix`, {
        scenario_ids: matrixSelected, estimate_id: scenarioTarget === 'a' ? idA : idB,
      }, { withCredentials: true });
      setMatrixData(res.data);
    } catch { /* error */ }
    finally { setMatrixLoading(false); }
  };

  const handleMatrixExport = async (format) => {
    if (matrixSelected.length < 2 || (!idA && !idB)) return;
    setMatrixExporting(true);
    try {
      const res = await axios.post(`${API_URL}/api/scenarios/compare-matrix/export`, {
        scenario_ids: matrixSelected, estimate_id: scenarioTarget === 'a' ? idA : idB, format,
      }, { responseType: 'blob', withCredentials: true });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `Scenario_Matrix.${format}`; a.click();
      URL.revokeObjectURL(url);
    } catch { /* error */ }
    finally { setMatrixExporting(false); }
  };

  const handleGetRecommendation = async () => {
    if (matrixSelected.length < 2 || (!idA && !idB)) return;
    setRecLoading(true); setRecData(null);
    try {
      const res = await axios.post(`${API_URL}/api/scenarios/recommend`, {
        scenario_ids: matrixSelected, estimate_id: scenarioTarget === 'a' ? idA : idB, priority: recPriority,
      }, { withCredentials: true });
      setRecData(res.data);
      const hRes = await axios.get(`${API_URL}/api/scenarios/recommendation-history`, { withCredentials: true });
      setRecHistory(hRes.data?.history || []);
    } catch { /* error */ }
    finally { setRecLoading(false); }
  };

  const handleDeleteHistory = async (entryId) => {
    try {
      await axios.delete(`${API_URL}/api/scenarios/recommendation-history/${entryId}`, { withCredentials: true });
      setRecHistory(prev => prev.filter(h => h.id !== entryId));
      if (expandedEntry === entryId) setExpandedEntry(null);
    } catch { /* error */ }
  };

  return (
    <Card className="mb-5 border-dashed border-[#4ABFCF]/40" data-testid="scenario-slider-panel">
      <button
        onClick={() => setScenarioOpen(!scenarioOpen)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50/50 transition-colors"
        data-testid="scenario-slider-toggle"
      >
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-[#4ABFCF]" />
          <span className="text-sm font-semibold text-[#0F2040]">Scenario Slider</span>
          {scenarioActive && <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-200 ml-1">Modified</Badge>}
          {scenarioLoading && <Loader2 className="w-3 h-3 animate-spin text-[#4ABFCF]" />}
        </div>
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${scenarioOpen ? 'rotate-90' : ''}`} />
      </button>

      {scenarioOpen && (
        <CardContent className="pt-0 pb-4 px-5">
          <p className="text-xs text-slate-500 mb-4">Tweak complexity and risk modifiers for one estimate to see how deltas change in real-time.</p>

          {/* Target selector + Reset */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-slate-600">Apply to:</label>
              <div className="flex rounded-lg border overflow-hidden">
                <button onClick={() => setScenarioTarget('a')}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${scenarioTarget === 'a' ? 'bg-[#0F2040] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                  data-testid="scenario-target-a">{nameA}</button>
                <button onClick={() => setScenarioTarget('b')}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors border-l ${scenarioTarget === 'b' ? 'bg-[#0F2040] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                  data-testid="scenario-target-b">{nameB}</button>
              </div>
            </div>
            {scenarioActive && (
              <Button variant="ghost" size="sm" onClick={resetScenario} className="text-xs text-slate-500 h-7" data-testid="scenario-reset-btn">
                <RotateCcw className="w-3 h-3 mr-1" /> Reset
              </Button>
            )}
          </div>

          {/* Complexity selector */}
          <div className="mb-4">
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Complexity</label>
            <div className="flex gap-2">
              {COMPLEXITY_OPTIONS.map(opt => (
                <button key={opt.value} onClick={() => handleComplexityChange(opt.value)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${scenarioComplexity === opt.value ? 'bg-[#4ABFCF] text-white border-[#4ABFCF]' : 'bg-white text-slate-500 border-slate-200 hover:border-[#4ABFCF]/50'}`}
                  data-testid={`scenario-complexity-${opt.value}`}>{opt.label}</button>
              ))}
            </div>
          </div>

          {/* Modifier sliders */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            {MODIFIER_DEFS.map(mod => {
              const val = scenarioModifiers[mod.key] ?? 1.0;
              const isDefault = Math.abs(val - 1.0) < 0.01;
              return (
                <div key={mod.key} data-testid={`scenario-mod-${mod.key}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-slate-600">{mod.label}</span>
                    <span className={`text-xs font-mono tabular-nums ${isDefault ? 'text-slate-400' : val > 1 ? 'text-red-500 font-semibold' : 'text-emerald-600 font-semibold'}`}>{val.toFixed(2)}</span>
                  </div>
                  <Slider value={[val]} min={mod.min} max={mod.max} step={mod.step}
                    onValueChange={([v]) => handleModifierChange(mod.key, parseFloat(v.toFixed(2)))}
                    className="w-full" data-testid={`scenario-slider-${mod.key}`} />
                  <div className="flex justify-between text-[9px] text-slate-300 mt-0.5">
                    <span>{mod.min}</span><span>1.0</span><span>{mod.max}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Save / Load scenario bar */}
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
            {showSaveInput ? (
              <div className="flex items-center gap-1.5">
                <input type="text" value={saveName} onChange={e => setSaveName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSaveScenario()}
                  placeholder="Scenario name..." className="border rounded-md px-2 py-1 text-xs w-40 focus:ring-1 focus:ring-[#4ABFCF] focus:border-[#4ABFCF]"
                  autoFocus data-testid="scenario-save-input" />
                <Button size="sm" onClick={handleSaveScenario} disabled={!saveName.trim()}
                  className="h-7 text-xs bg-[#4ABFCF] hover:bg-[#3AAFBF]" data-testid="scenario-save-confirm">
                  <Save className="w-3 h-3 mr-1" /> Save
                </Button>
                <button onClick={() => { setShowSaveInput(false); setSaveName(''); }}
                  className="text-slate-400 hover:text-slate-600" data-testid="scenario-save-cancel"><X className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setShowSaveInput(true)}
                className="h-7 text-xs" data-testid="scenario-save-btn">
                <Save className="w-3 h-3 mr-1" /> Save Scenario
              </Button>
            )}

            {/* Load */}
            <div className="relative" ref={loadRef}>
              <Button variant="outline" size="sm" onClick={() => setLoadOpen(!loadOpen)} className="h-7 text-xs" data-testid="scenario-load-btn">
                <FolderOpen className="w-3 h-3 mr-1" /> Load
                {savedScenarios.length > 0 && <span className="ml-1 bg-slate-200 text-slate-600 rounded-full px-1.5 text-[9px] font-semibold">{savedScenarios.length}</span>}
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
              {loadOpen && (
                <div className="absolute left-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 py-1 min-w-[260px] max-h-[240px] overflow-y-auto" data-testid="scenario-load-dropdown">
                  {savedScenarios.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-slate-400">No saved scenarios yet</p>
                  ) : (
                    <>
                      <p className="px-3 pt-1.5 pb-1 text-[9px] text-slate-400 font-medium uppercase tracking-wide">Click to load &middot; Check to compare</p>
                      {savedScenarios.map(s => (
                        <div key={s.id} className="flex items-center gap-1.5 px-3 py-1.5 hover:bg-slate-50 group">
                          <input type="checkbox" checked={matrixSelected.includes(s.id)} onChange={() => toggleMatrixSelect(s.id)}
                            className="w-3.5 h-3.5 rounded border-slate-300 text-[#4ABFCF] focus:ring-[#4ABFCF] cursor-pointer shrink-0"
                            data-testid={`scenario-check-${s.id}`} onClick={e => e.stopPropagation()} />
                          <button onClick={() => handleLoadScenario(s)} className="text-left flex-1 min-w-0" data-testid={`scenario-load-item-${s.id}`}>
                            <p className="text-xs font-medium text-[#0F2040] truncate">{s.name}</p>
                            <p className="text-[9px] text-slate-400">{s.complexity} &middot; {s.target === 'a' ? 'Est A' : 'Est B'}</p>
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDeleteScenario(s.id); }}
                            className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 ml-1 transition-opacity"
                            data-testid={`scenario-delete-${s.id}`}><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>

            {matrixSelected.length >= 2 && (
              <Button size="sm" onClick={runMatrix} disabled={matrixLoading}
                className="h-7 text-xs bg-[#0F2040] hover:bg-[#1a3060]" data-testid="scenario-compare-matrix-btn">
                {matrixLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Rows3 className="w-3 h-3 mr-1" />}
                Compare {matrixSelected.length} Scenarios
              </Button>
            )}
          </div>

          {/* Matrix View */}
          {matrixOpen && matrixData && (
            <div className="mt-4 pt-3 border-t border-slate-100" data-testid="scenario-matrix">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Rows3 className="w-4 h-4 text-[#4ABFCF]" />
                  <span className="text-xs font-semibold text-[#0F2040]">Scenario Comparison Matrix</span>
                  <Badge className="text-[8px] bg-slate-100 text-slate-500">{matrixData.columns?.length} scenarios</Badge>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button variant="outline" size="sm" onClick={() => handleMatrixExport('pdf')} disabled={matrixExporting} className="h-6 text-[10px] px-2" data-testid="matrix-export-pdf">
                    {matrixExporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3 mr-1" />} PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleMatrixExport('xlsx')} disabled={matrixExporting} className="h-6 text-[10px] px-2" data-testid="matrix-export-xlsx">
                    {matrixExporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3 mr-1" />} Excel
                  </Button>
                  <button onClick={() => { setMatrixOpen(false); setMatrixData(null); }} className="text-slate-400 hover:text-slate-600 ml-1" data-testid="scenario-matrix-close">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="scenario-matrix-table">
                  <thead>
                    <tr className="bg-[#0F2040] text-white">
                      <th className="text-left px-3 py-2 font-medium sticky left-0 bg-[#0F2040] z-10">Metric</th>
                      {(matrixData.columns || []).map(col => (
                        <th key={col.id} className="text-center px-3 py-2 font-medium min-w-[110px]">
                          <span className="block truncate">{col.name}</span>
                          <span className="text-[9px] font-normal opacity-70">{col.complexity}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: 'total_effort', label: 'Total Effort', fmt: v => `${v.toLocaleString()}h` },
                      { key: 'total_cost', label: 'Total Cost', fmt: v => `${currencySymbol}${v.toLocaleString()}` },
                      { key: 'confidence', label: 'Confidence', fmt: v => `${v}%` },
                      { key: 'complexity_score', label: 'Complexity', fmt: v => v.toFixed(1) },
                      { key: 'component_count', label: 'Components', fmt: v => v },
                    ].map(({ key, label, fmt }, ri) => {
                      const bestIds = matrixData.best?.[key] || [];
                      return (
                        <tr key={key} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="px-3 py-2 font-medium text-[#0F2040] sticky left-0 bg-inherit z-10">{label}</td>
                          {(matrixData.columns || []).map(col => {
                            const isBest = bestIds.includes(col.id);
                            return (
                              <td key={col.id} className={`px-3 py-2 text-center ${isBest ? 'bg-emerald-50' : ''}`}>
                                <span className={isBest ? 'font-semibold text-emerald-700' : 'text-slate-600'}>{fmt(col[key] ?? 0)}</span>
                                {isBest && <Trophy className="w-3 h-3 text-emerald-500 inline ml-1" />}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* AI Recommendation */}
              <div className="mt-4 pt-3 border-t border-slate-100" data-testid="scenario-recommend-section">
                <div className="flex items-center gap-2 flex-wrap">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-semibold text-[#0F2040]">AI Recommendation</span>
                  <select value={recPriority} onChange={e => { setRecPriority(e.target.value); setRecData(null); }}
                    className="border rounded-md px-2 py-1 text-xs bg-white focus:ring-1 focus:ring-[#4ABFCF]" data-testid="scenario-rec-priority">
                    <option value="minimize_cost">Minimize Cost</option>
                    <option value="minimize_effort">Minimize Effort</option>
                    <option value="fastest_delivery">Fastest Delivery</option>
                    <option value="balanced">Balanced</option>
                  </select>
                  <Button size="sm" onClick={handleGetRecommendation} disabled={recLoading}
                    className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white" data-testid="scenario-rec-btn">
                    {recLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                    {recLoading ? 'Analyzing...' : 'Get Recommendation'}
                  </Button>
                </div>

                {recData?.recommendation && (
                  <div className="mt-3 bg-amber-50/50 border border-amber-200/60 rounded-lg p-4 space-y-3" data-testid="scenario-rec-result">
                    <div className="flex items-start gap-2">
                      <Trophy className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-[#0F2040]">Recommended: <span className="text-amber-700">{recData.recommendation.recommended_scenario}</span></p>
                        <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{recData.recommendation.reasoning}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Lightbulb className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Trade-offs</p>
                        <p className="text-xs text-slate-600 leading-relaxed">{recData.recommendation.tradeoffs}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Risk</p>
                        <p className="text-xs text-slate-600 leading-relaxed">{recData.recommendation.risk_callout}</p>
                      </div>
                    </div>
                    {recData.recommendation.alternative && (
                      <div className="text-xs text-slate-500 border-t border-amber-200/40 pt-2 mt-1">
                        <span className="font-medium">Runner-up:</span> {recData.recommendation.alternative}
                      </div>
                    )}
                  </div>
                )}

                {/* Recommendation History */}
                <div className="mt-3" data-testid="rec-history-section">
                  <button onClick={() => setHistoryOpen(!historyOpen)}
                    className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors" data-testid="rec-history-toggle">
                    <History className="w-3.5 h-3.5" />
                    <span className="font-medium">Past Recommendations</span>
                    {recHistory.length > 0 && <span className="bg-slate-200 text-slate-600 rounded-full px-1.5 text-[9px] font-semibold">{recHistory.length}</span>}
                    <ChevronRight className={`w-3 h-3 transition-transform ${historyOpen ? 'rotate-90' : ''}`} />
                  </button>

                  {historyOpen && (
                    <div className="mt-2 max-h-[260px] overflow-y-auto space-y-1.5" data-testid="rec-history-list">
                      {recHistory.length === 0 ? (
                        <p className="text-xs text-slate-400 py-2">No past recommendations yet. Generate one above.</p>
                      ) : recHistory.map(entry => {
                        const isExpanded = expandedEntry === entry.id;
                        const rec = entry.recommendation || {};
                        const date = entry.created_at ? new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                        return (
                          <div key={entry.id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`rec-history-entry-${entry.id}`}>
                            <div className="flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 transition-colors">
                              <button onClick={() => setExpandedEntry(isExpanded ? null : entry.id)}
                                className="flex items-center gap-2 text-left flex-1 min-w-0" data-testid={`rec-history-expand-${entry.id}`}>
                                <ChevronRight className={`w-3 h-3 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <Trophy className="w-3 h-3 text-amber-500 shrink-0" />
                                    <span className="text-xs font-medium text-[#0F2040] truncate">{rec.recommended_scenario || 'N/A'}</span>
                                    <Badge variant="outline" className="text-[8px] shrink-0">{entry.priority_label || entry.priority}</Badge>
                                  </div>
                                  <p className="text-[9px] text-slate-400 mt-0.5">{date} &middot; {entry.estimate_name || ''}</p>
                                </div>
                              </button>
                              <button onClick={() => handleDeleteHistory(entry.id)}
                                className="text-slate-300 hover:text-red-500 ml-2 shrink-0 transition-colors" data-testid={`rec-history-delete-${entry.id}`}>
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                            {isExpanded && (
                              <div className="px-3 py-2.5 space-y-2 bg-white border-t border-slate-100" data-testid={`rec-history-detail-${entry.id}`}>
                                <div><p className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">Reasoning</p><p className="text-xs text-slate-600 leading-relaxed">{rec.reasoning}</p></div>
                                <div><p className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">Trade-offs</p><p className="text-xs text-slate-600 leading-relaxed">{rec.tradeoffs}</p></div>
                                <div><p className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">Risk</p><p className="text-xs text-slate-600 leading-relaxed">{rec.risk_callout}</p></div>
                                {rec.alternative && <p className="text-xs text-slate-500 border-t border-slate-100 pt-1.5"><span className="font-medium">Runner-up:</span> {rec.alternative}</p>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
